import type { ProductFile } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";

/**
 * Product gallery media classification.
 *
 * Norce product `Files` mixes images with two kinds of video:
 * - uploaded mp4 — `Extension === ".mp4"` (group `Code` "gallery-video",
 *   `Type` 4); `Path` is the playable CDN URL.
 * - embedded — group `Code` "embedded" (`Type` 6), `Key` is null so `Path`
 *   resolves to `.../null`. The link itself currently arrives in `Description`
 *   (Norce admin "Video link" fallback) until norce-pwa preserves it on `Path`.
 */
export type ProductMediaKind = "image" | "video" | "youtube";

/** Structural subset shared by ProductFile / CategoryFiles / FilteredProductItemFiles. */
type MediaFileLike = Pick<
	ProductFile,
	"Path" | "Description" | "Extension" | "Code"
>;

const YOUTUBE_URL =
	/(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([\w-]{6,})/i;

/** Extract a YouTube video id from any common URL form. */
export const getYoutubeId = (url?: string | null): string | null =>
	url?.match(YOUTUBE_URL)?.[1] ?? null;

/**
 * Resolve an embedded video's link. Prefer `Path` (once norce-pwa exposes the
 * real link there), fall back to `Description` (the current Norce workaround).
 */
export const getEmbeddedLink = (file: MediaFileLike): string | null =>
	[file.Path, file.Description].find((value) => getYoutubeId(value)) ?? null;

export const getProductMediaKind = (file: MediaFileLike): ProductMediaKind => {
	if (file.Code === "embedded" || getEmbeddedLink(file)) {
		return "youtube";
	}
	if (file.Extension?.toLowerCase().replace(/^\./, "") === "mp4") {
		return "video";
	}
	return "image";
};

export const isProductImage = (file: MediaFileLike): boolean =>
	getProductMediaKind(file) === "image";

/**
 * A media entry is renderable when it's an image, an mp4 with a path, or an
 * embedded video with a resolvable link. Embedded entries without a link
 * (norce-pwa not yet patched and no Description fallback) are dropped so the
 * gallery never shows a broken slide.
 */
export const isRenderableMedia = (file: MediaFileLike): boolean => {
	const kind = getProductMediaKind(file);
	if (kind === "youtube") return Boolean(getEmbeddedLink(file));
	if (kind === "video") return Boolean(file.Path);
	return true;
};

const IFRAME_TAG = /<iframe\b[^>]*>(?:[\s\S]*?<\/iframe>)?/gi;
const SRC_ATTR = /\bsrc\s*=\s*["']([^"']+)["']/i;

export type RichTextSegment =
	| { type: "html"; html: string }
	| { type: "video"; url: string };

/**
 * A rich-text segment ready to render: sanitized markup, or a video to hand to
 * `ProductGalleryVideo`. Exactly one field is set per part.
 */
export interface RichTextPart {
	html?: string;
	video?: ProductFile;
}

/**
 * Split CMS/PIM rich text into markup and embedded videos, in document order.
 *
 * `sanitizeHtml` neutralizes `<iframe>` on purpose — product descriptions come
 * from Norce and must not be able to embed arbitrary documents — but js-xss
 * escapes unknown tags rather than dropping them, so an embed left in the
 * markup renders as visible `<iframe …>` text. Splitting instead of stripping
 * lets the caller sanitize each markup chunk and render the video *where the
 * author put it*, through `ProductGalleryVideo`: the player loads only on
 * click, so no third-party cookies before consent and no ~700kB of YouTube JS
 * on page load.
 *
 * Iframes pointing anywhere else are dropped: they can't be rendered safely,
 * and showing their markup as text is worse than showing nothing.
 *
 * Plain string work, no DOM — identical output on server and client, so it can
 * run before `sanitizeHtml` without risking a hydration mismatch.
 */
export const splitRichTextMedia = (html?: string | null): RichTextSegment[] => {
	if (!html) return [];

	const segments: RichTextSegment[] = [];
	let cursor = 0;

	for (const match of html.matchAll(IFRAME_TAG)) {
		const tag = match[0];
		const start = match.index ?? 0;
		const before = html.slice(cursor, start);

		if (before.trim()) segments.push({ type: "html", html: before });

		const src = tag.match(SRC_ATTR)?.[1];
		if (src && getYoutubeId(src)) segments.push({ type: "video", url: src });

		cursor = start + tag.length;
	}

	const rest = html.slice(cursor);
	if (rest.trim()) segments.push({ type: "html", html: rest });

	return segments;
};
